import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FaFacebookF, FaInstagram, FaAngleUp } from "react-icons/fa";
import { useI18n } from "../contexts/I18n";
import "./Footer.css";

const FOOTER_SERVICES = [
  { title: "Barber", slug: "barber", ar: "حلاق" },
  { title: "Hair & Nail", slug: "hair-&-nail", ar: "صالون شعر وأظافر" },
  { title: "Laser Clinics", slug: "laser-clinics", ar: "عيادات الليزر" },
  { title: "Makeup", slug: "makeup", ar: "مكياج" },
  { title: "Massage", slug: "massage", ar: "مساج" },
];

export default function Footer() {
  const { t, lang } = useI18n();
  const [showTop, setShowTop] = useState(false);
  const isAr = lang === "ar";

  const reducedMotion = useMemo(
    () =>
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches,
    []
  );

  useEffect(() => {
    const onScroll = () => setShowTop(window.scrollY > 320);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollToTop = () =>
    window.scrollTo({
      top: 0,
      behavior: reducedMotion ? "auto" : "smooth",
    });

  const TOP_LINKS = [
    { id: "about", label: t("about"), to: "/about", hideOnDesktopTopNav: true },
    { id: "team", label: t("team"), to: "/team" },
    { id: "list-biz", label: t("listBiz"), to: "/ListUrBussiness", hideOnDesktopTopNav: true },
    { id: "for-business", label: lang === "ar" ? "للأعمال" : "For Business", to: "/for-business", hideOnDesktopTopNav: true },
    { id: "faq", label: t("footerFaq"), to: "/faq" },
    { id: "privacy", label: t("privacy"), to: "/privacy", hideOnMobile: true },
    { id: "support", label: t("support"), to: "/support", hideOnMobile: true },
    { id: "contact", label: t("contact"), to: "/contact" },
  ];

  const LEGAL_LINKS = [
    { id: "about", label: t("about"), to: "/about" },
    { id: "privacy", label: t("privacy"), to: "/privacy", hideOnMobile: true },
    { id: "faq", label: t("footerFaq"), to: "/faq" },
    { id: "support", label: t("support"), to: "/support", hideOnMobile: true },
    { id: "contact", label: t("contact"), to: "/contact" },
    { id: "delete-account", label: t("deleteAccount"), to: "/delete-account" },
  ];

  const COMPANY_LINKS = [
    { id: "about", label: t("about"), to: "/about" },
    { id: "team", label: t("team"), to: "/team" },
    { id: "for-business", label: lang === "ar" ? "للأعمال" : "For Business", to: "/for-business" },
    { id: "faq", label: t("footerFaq"), to: "/faq" },
    { id: "list-biz", label: t("listBiz"), to: "/ListUrBussiness" },
    { id: "delete-account", label: t("deleteAccount"), to: "/delete-account" },
  ];

  const mobileFooterLinks = (links) => links.filter((link) => !link.hideOnMobile);
  const desktopTopNavLinks = (links) => links.filter((link) => !link.hideOnDesktopTopNav);

  const SOCIAL_LINKS = [
    {
      Icon: FaFacebookF,
      url: "https://www.facebook.com/share/184zUxhmEW/?mibextid=wwXIfr",
      label: "Facebook",
    },
    {
      Icon: FaInstagram,
      url: "https://www.instagram.com/bookwithehgezly?igsh=M3kzOTh1YjRmdmli",
      label: "Instagram",
    },
  ];

  return (
    <>
      <div className="section-wave section-wave--footer" aria-hidden="true" />
      <footer
        className={`site-footer ${isAr ? "rtl" : ""}`}
        dir={isAr ? "rtl" : "ltr"}
      >
        <div className="site-footer__inner">
          {/* Mobile — Talabat-style accordion */}
          <div className="site-footer__mobile">
            <details className="site-footer__accordion">
              <summary>{t("footerPopularServices")}</summary>
              <div className="site-footer__accordion-panel">
                <ul className="site-footer__list">
                  {FOOTER_SERVICES.map(({ title, slug, ar }) => (
                    <li key={slug}>
                      <Link
                        to={`/service/${slug}`}
                        className="site-footer__link"
                      >
                        {isAr ? ar : title}
                      </Link>
                    </li>
                  ))}
                </ul>
                <Link to="/explore" className="site-footer__more">
                  {t("footerMoreServices")}
                </Link>
              </div>
            </details>

            <details className="site-footer__accordion">
              <summary>{t("quickLinks")}</summary>
              <div className="site-footer__accordion-panel">
                <ul className="site-footer__list">
                  {mobileFooterLinks(TOP_LINKS).map(({ id, label, to }) => (
                    <li key={id}>
                      <Link to={to} className="site-footer__link">
                        {label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </details>

            <div className="site-footer__socialBlock">
              <p className="site-footer__socialLabel">{t("footerFollowUs")}</p>
              <div className="site-footer__socials">
                {SOCIAL_LINKS.map(({ Icon, url, label }) => (
                  <a
                    key={label}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={label}
                    className="site-footer__socialBtn"
                    title={label}
                  >
                    <Icon />
                  </a>
                ))}
              </div>
            </div>

            <nav className="site-footer__legal" aria-label={t("quickLinks")}>
              {mobileFooterLinks(LEGAL_LINKS).map(({ id, label, to }, index) => (
                <React.Fragment key={id}>
                  {index > 0 && (
                    <span className="site-footer__legalSep" aria-hidden="true">
                      ·
                    </span>
                  )}
                  <Link to={to} className="site-footer__legalLink">
                    {label}
                  </Link>
                </React.Fragment>
              ))}
            </nav>

            <p className="site-footer__supportLine">{t("footerSupportLine")}</p>

            <p className="site-footer__copy site-footer__copy--mobile">
              © {new Date().getFullYear()} {t("brand")}.{" "}
              {isAr ? "جميع الحقوق محفوظة" : "All rights reserved."}
            </p>
          </div>

          {/* Desktop — multi-column layout */}
          <div className="site-footer__desktop">
            <nav className="site-footer__topnav" aria-label={t("quickLinks")}>
              {desktopTopNavLinks(TOP_LINKS).map(({ id, label, to }) => (
                <Link key={id} to={to} className="site-footer__toplink">
                  {label}
                </Link>
              ))}
            </nav>

            <hr className="site-footer__rule" />

            <div className="site-footer__grid">
              <section
                className="site-footer__col"
                aria-labelledby="footer-services"
              >
                <h3 id="footer-services" className="site-footer__heading">
                  {t("footerPopularServices")}
                </h3>
                <ul className="site-footer__list">
                  {FOOTER_SERVICES.map(({ title, slug, ar }) => (
                    <li key={slug}>
                      <Link
                        to={`/service/${slug}`}
                        className="site-footer__link"
                      >
                        {isAr ? ar : title}
                      </Link>
                    </li>
                  ))}
                </ul>
                <Link to="/explore" className="site-footer__more">
                  {t("footerMoreServices")}
                </Link>
              </section>

              <section
                className="site-footer__col"
                aria-labelledby="footer-company"
              >
                <h3 id="footer-company" className="site-footer__heading">
                  {t("quickLinks")}
                </h3>
                <ul className="site-footer__list">
                  {COMPANY_LINKS.map(({ id, label, to }) => (
                    <li key={id}>
                      <Link to={to} className="site-footer__link">
                        {label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </section>

              <section
                className="site-footer__col site-footer__col--social"
                aria-labelledby="footer-social"
              >
                <h3 id="footer-social" className="site-footer__heading">
                  {t("footerFollowUs")}
                </h3>
                <div className="site-footer__socials">
                  {SOCIAL_LINKS.map(({ Icon, url, label }) => (
                    <a
                      key={label}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={label}
                      className="site-footer__socialBtn"
                      title={label}
                    >
                      <Icon />
                    </a>
                  ))}
                </div>
              </section>
            </div>

            <hr className="site-footer__rule" />

            <div className="site-footer__bar">
              <p className="site-footer__copy">
                © {new Date().getFullYear()} {t("brand")}.{" "}
                {isAr ? "جميع الحقوق محفوظة" : "All rights reserved."}
              </p>
            </div>
          </div>
        </div>

        {showTop && (
          <button
            type="button"
            className="site-footer__top"
            onClick={scrollToTop}
            aria-label={t("backToTop")}
            title={t("backToTop")}
          >
            <FaAngleUp />
          </button>
        )}
      </footer>
    </>
  );
}
